import { useEffect, useRef, useState, type FormEvent } from 'react';
import { CheckCircle2, Mic, MicOff, TriangleAlert, Undo2 } from 'lucide-react';
import { HelpBubble } from '@/help/HelpBubble';
import { setCount } from '@/data/nights';
import { matchDrinks, parseVoiceCount, pickDrink, type VoiceCommand } from '@/domain/voice';
import { SECTION_KIND, type AreaId, type Item, type SectionId } from '@/domain/types';
import { Button, cn } from '@/ui/core';
import { TextInput } from '@/ui/form';
import { canListen, listenOnce, speechErrorText, type Listener } from '@/lib/speech';

export interface VoiceRow {
  item: Item;
  section: SectionId;
  value: number | null;
}

type Count = Extract<VoiceCommand, { kind: 'count' }>;
interface Done {
  row: VoiceRow;
  before: number | null;
  after: number;
  detail: string;
}

const EXAMPLE: Record<string, string> = { back_beer: 'e.g. “Coors Light 2 boxes and 5”', front_beer: 'e.g. “Coors Light 18”', liquor: 'e.g. “Crown Royal 23.6”' };
/** Stop listening after this long with nothing heard, so the tablet isn't left listening. */
const IDLE_MS = 90_000;

/**
 * Counting by voice on an area's page. Say (or type) the drink and how many; it's saved to
 * that drink's box right away and shown in green with an Undo. With no microphone in the app
 * (an iPad home-screen app), the keyboard's microphone key types into the box instead.
 */
export function VoiceBar({ date, area, rows }: { date: string; area: AreaId; rows: VoiceRow[] }) {
  const [text, setText] = useState('');
  const [hearing, setHearing] = useState('');
  const [listening, setListening] = useState(false);
  const [last, setLast] = useState<Done | null>(null);
  const [problem, setProblem] = useState<string | null>(null);
  const [choices, setChoices] = useState<{ cmd: Count; options: VoiceRow[] } | null>(null);
  const listener = useRef<Listener | null>(null);
  const keepOn = useRef(false);
  /** The short pause before listening again for the next phrase. */
  const restart = useRef<number | undefined>(undefined);
  const lastHeard = useRef(0);
  // Read inside speech callbacks, which outlive a render.
  const rowsRef = useRef(rows);
  rowsRef.current = rows;
  const lastRef = useRef(last);
  lastRef.current = last;
  const mic = canListen();

  const stop = () => {
    keepOn.current = false;
    window.clearTimeout(restart.current);
    restart.current = undefined;
    listener.current?.stop();
    listener.current = null;
    setListening(false);
    setHearing('');
  };
  useEffect(() => {
    const away = () => document.visibilityState === 'hidden' && stop();
    document.addEventListener('visibilitychange', away);
    return () => {
      document.removeEventListener('visibilitychange', away);
      keepOn.current = false;
      window.clearTimeout(restart.current);
      listener.current?.stop();
    };
  }, []);

  const highlight = (id: string) => {
    const el = document.getElementById(`row-${id}`);
    el?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    el?.classList.add('voice-flash');
    window.setTimeout(() => el?.classList.remove('voice-flash'), 1600);
  };

  const apply = async (row: VoiceRow, cmd: Count) => {
    setChoices(null);
    const { item, section } = row;
    let value: number | null;
    let detail = '';
    if (cmd.boxes != null) {
      if (SECTION_KIND[section] !== 'beer') return setProblem(`${item.name} is counted in ounces, not boxes.`);
      if (!item.caseSize) return setProblem(`${item.name} has no box size yet. Say the total bottles, or a manager sets the box size in Drink lists.`);
      value = cmd.boxes * item.caseSize + (cmd.singles ?? 0);
      detail = ` (${cmd.boxes} box${cmd.boxes === 1 ? '' : 'es'} of ${item.caseSize}${cmd.singles ? ` + ${cmd.singles}` : ''})`;
    } else value = cmd.total ?? cmd.singles;
    if (value == null) return setProblem('Didn’t hear how many.');
    // The number in the box now (a "Which one?" choice may have been offered a while ago).
    const before = rowsRef.current.find((r) => r.item.id === item.id && r.section === section)?.value ?? row.value;
    try {
      await setCount(date, section, item.id, value);
    } catch (err) {
      return setProblem((err as Error).message);
    }
    setProblem(null);
    setLast({ row, before, after: value, detail });
    highlight(item.id);
  };

  const undo = async () => {
    const l = lastRef.current;
    if (!l) return setProblem('Nothing to undo.');
    // Changed by hand (or on the other device) since: don't put the old number back over it.
    const now = rowsRef.current.find((r) => r.item.id === l.row.item.id && r.section === l.row.section)?.value ?? null;
    if (now !== l.after) {
      setLast(null);
      return setProblem(`${l.row.item.name} has been changed since, so it wasn’t undone. Fix it in its box if needed.`);
    }
    try {
      await setCount(date, l.row.section, l.row.item.id, l.before);
      setLast(null);
      setProblem(null);
      highlight(l.row.item.id);
    } catch (err) {
      setProblem((err as Error).message);
    }
  };

  const handle = async (phrase: string) => {
    lastHeard.current = Date.now();
    const cmd = parseVoiceCount(phrase);
    if (cmd.kind === 'undo') return undo();
    if (cmd.kind === 'unknown') return setProblem(`${cmd.reason} Heard: “${phrase}”.`);
    const matches = matchDrinks(cmd.drinkText, rowsRef.current.map((r) => ({ name: r.item.name, row: r })));
    const picked = pickDrink(matches);
    if (picked) return apply(picked.row, cmd);
    if (matches.length) {
      setProblem(null);
      return setChoices({ cmd, options: matches.slice(0, 3).map((m) => m.item.row) });
    }
    setProblem(`No drink on this list sounds like “${cmd.drinkText}”. Heard: “${phrase}”.`);
  };

  const listen = () => {
    listener.current = listenOnce({
      onHearing: setHearing,
      onPhrase: (t) => {
        setHearing('');
        void handle(t);
      },
      onError: (code) => {
        const msg = speechErrorText(code);
        if (msg) {
          setProblem(msg);
          keepOn.current = false;
        }
      },
      onEnd: () => {
        listener.current = null;
        if (keepOn.current && Date.now() - lastHeard.current > IDLE_MS) {
          setProblem('Stopped listening: nothing heard for a while. Tap “Count by voice” to start again.');
          keepOn.current = false;
        }
        if (keepOn.current)
          restart.current = window.setTimeout(() => {
            restart.current = undefined;
            if (keepOn.current && !listener.current) listen();
          }, 200);
        else stop();
      },
    });
    if (!listener.current) {
      keepOn.current = false;
      setListening(false);
      setProblem('Voice couldn’t start here. Use the microphone on the keyboard, or type the count.');
    }
  };

  const start = () => {
    if (listener.current || restart.current) return;
    setProblem(null);
    setChoices(null);
    keepOn.current = true;
    lastHeard.current = Date.now();
    setListening(true);
    listen();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    const t = text.trim();
    if (!t) return;
    setText('');
    void handle(t);
  };

  return (
    // Stays in view while scrolling on a tablet; on a phone that would leave no room for the list.
    <section aria-label="Count by voice" className="z-20 flex flex-col gap-2 rounded-2xl border-2 border-info/50 bg-surface/95 p-2.5 shadow-md backdrop-blur md:sticky md:top-24">
      <div className="flex flex-wrap items-center gap-2">
        {mic && (
          <Button size="lg" variant={listening ? 'danger' : 'primary'} icon={listening ? <MicOff className="size-5" aria-hidden /> : <Mic className="size-5" aria-hidden />} onClick={listening ? stop : start}>
            {listening ? 'Stop listening' : 'Count by voice'}
          </Button>
        )}
        <form onSubmit={submit} className="flex min-w-0 flex-[1_1_16rem] items-center gap-2">
          <TextInput aria-label="Say or type a drink and how many" value={listening && hearing ? hearing : text} onChange={(e) => setText(e.target.value)} placeholder={EXAMPLE[area]} enterKeyHint="go" autoComplete="off" className="min-h-12 min-w-0 flex-1 text-lg" />
          <Button type="submit" size="lg" disabled={!text.trim()}>
            Add
          </Button>
        </form>
        <HelpBubble topic="voice-count" size="sm" />
      </div>
      {listening ? (
        <p className="flex items-center gap-2 text-base font-semibold text-info">
          <span className="size-2.5 animate-pulse rounded-full bg-danger" aria-hidden /> Listening. Say the drink, then how many.
        </p>
      ) : (
        !mic && <p className="text-sm text-muted">To talk instead of type: tap the box, then the microphone on the keyboard. Say the drink and how many, then tap Add.</p>
      )}
      {last && !choices && (
        <p className="flex flex-wrap items-center gap-2 text-base" aria-live="polite">
          <CheckCircle2 className="size-5 shrink-0 text-ok" aria-hidden />
          <span className="min-w-0 flex-1">
            <strong>{last.row.item.name}</strong>: {last.after.toLocaleString('en-CA')}
            {SECTION_KIND[last.row.section] === 'liquor' ? ' oz' : ''}
            {last.detail}
            {last.before != null && last.before !== last.after && <span className="text-muted"> (was {last.before.toLocaleString('en-CA')})</span>}
          </span>
          <Button size="sm" variant="ghost" icon={<Undo2 className="size-4" aria-hidden />} onClick={() => void undo()}>
            Undo
          </Button>
        </p>
      )}
      {choices && (
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-base font-semibold">Which one?</span>
          {choices.options.map((o) => (
            <Button key={`${o.section}-${o.item.id}`} onClick={() => void apply(o, choices.cmd)}>
              {o.item.name}
            </Button>
          ))}
          <Button variant="ghost" onClick={() => setChoices(null)}>
            None of these
          </Button>
        </div>
      )}
      {problem && (
        <p role="alert" className={cn('flex items-start gap-2 rounded-xl bg-warn-soft p-2 text-base')}>
          <TriangleAlert className="mt-0.5 size-5 shrink-0 text-warn" aria-hidden />
          <span>{problem}</span>
        </p>
      )}
    </section>
  );
}
